/**
 * Client delle API v3 (vedi ../../spec/API.md).
 * Errori tipizzati: il chiamante decide cosa fare (401 -> pairing, 403 -> disattivato, 402 -> abbonamento).
 */
import type { Capabilities, Platform } from '../types';

export class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'HttpError';
  }
}
/** 401: token invalido o revocato. */
export class AuthError extends HttpError {
  constructor() { super(401, 'non autorizzato'); this.name = 'AuthError'; }
}
/** 403 device_disabled. */
export class DeviceDisabledError extends HttpError {
  constructor() { super(403, 'schermo disattivato'); this.name = 'DeviceDisabledError'; }
}
/** 402 subscription_inactive. */
export class SubscriptionInactiveError extends HttpError {
  constructor() { super(402, 'abbonamento non attivo'); this.name = 'SubscriptionInactiveError'; }
}
/** Timeout, DNS, connessione rifiutata, offline, risposta illeggibile. */
export class NetworkError extends Error {
  constructor(message: string) { super(message); this.name = 'NetworkError'; }
}

export interface PairStartResponse {
  code: string;
  poll_token: string;
  expires_at: string;
  poll_interval_s: number;
}
export type PairStatusResult =
  | { status: 'pending' }
  | { status: 'paired'; device_token: string; device: { id: number; name: string } }
  | { status: 'restart' }; // 410 expired/already_delivered, 404

export interface Command {
  id: number;
  kind: string;
  payload?: unknown;
}
export interface HeartbeatResponse {
  server_time?: string;
  server_time_ms?: number;
  manifest_version?: string;
  commands: Command[];
}
export type ManifestResult =
  | { kind: 'ok'; manifest: unknown; etag: string | null }
  | { kind: 'not_modified' };

export interface HeartbeatBody {
  app_version: string;
  platform: Platform;
  capabilities: Capabilities;
  status: Record<string, unknown>;
}
export interface NowBody {
  schedule_id: string | null;
  asset_id: string | null;
  started_at: string;
  duration_ms: number | null;
}
export interface PairStartBody {
  platform: Platform;
  model: string;
  app_version: string;
  capabilities: Capabilities;
}

export interface ApiOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/** Backoff esponenziale 5 s -> 5 min (API.md §3). `attempt` parte da 0. */
export function backoffMs(attempt: number, jitter: () => number = Math.random): number {
  const base = Math.min(5_000 * 2 ** Math.max(0, attempt), 300_000);
  return Math.round(base * (0.85 + 0.3 * jitter()));
}

export class ApiClient {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(opts: ApiOptions) {
    this.base = opts.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
    this.timeoutMs = opts.timeoutMs ?? 15_000;
  }

  private async request(method: 'GET' | 'POST', path: string, opts: { token?: string; body?: unknown; form?: FormData; headers?: Record<string, string>; timeoutMs?: number } = {}): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
    if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
    let body: string | FormData | undefined;
    if (opts.form) {
      body = opts.form; // il browser imposta Content-Type multipart con il boundary
    } else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs);
    try {
      return await this.fetchImpl(this.base + path, { method, headers, body, signal: ctrl.signal, cache: 'no-store' });
    } catch (e) {
      const reason = ctrl.signal.aborted ? 'timeout' : (e instanceof Error ? e.message : 'errore di rete');
      throw new NetworkError(`${method} ${path}: ${reason}`);
    } finally {
      clearTimeout(timer);
    }
  }

  private async json<T>(res: Response): Promise<T> {
    try {
      return (await res.json()) as T;
    } catch {
      throw new NetworkError('risposta non valida');
    }
  }

  /** Mappa gli status degli endpoint autenticati (API.md §2). */
  private async checkAuthed(res: Response): Promise<void> {
    if (res.status === 401) throw new AuthError();
    if (res.status === 402) throw new SubscriptionInactiveError();
    if (res.status === 403) {
      let err = '';
      try {
        err = ((await res.clone().json()) as { error?: string }).error ?? '';
      } catch { /* corpo non JSON */ }
      if (err === 'device_disabled' || err === '') throw new DeviceDisabledError();
      throw new HttpError(403, `403 ${err}`);
    }
  }

  async pairStart(body: PairStartBody): Promise<PairStartResponse> {
    const res = await this.request('POST', '/api/v3/pair/start', { body });
    if (res.status !== 201 && res.status !== 200) throw new HttpError(res.status, `pair/start ${res.status}`);
    return this.json<PairStartResponse>(res);
  }

  async pairStatus(pollToken: string): Promise<PairStatusResult> {
    const res = await this.request('POST', '/api/v3/pair/status', { body: { poll_token: pollToken } });
    if (res.status === 404 || res.status === 410) return { status: 'restart' };
    if (res.status !== 200) throw new HttpError(res.status, `pair/status ${res.status}`);
    const j = await this.json<{ status: string; device_token?: string; device?: { id: number; name: string } }>(res);
    if (j.status === 'paired' && j.device_token) {
      return { status: 'paired', device_token: j.device_token, device: j.device ?? { id: 0, name: '' } };
    }
    return { status: 'pending' };
  }

  async manifest(token: string, etag?: string | null): Promise<ManifestResult> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;
    const res = await this.request('GET', '/api/v3/manifest', { token, headers });
    if (res.status === 304) return { kind: 'not_modified' };
    await this.checkAuthed(res);
    if (res.status !== 200) throw new HttpError(res.status, `manifest ${res.status}`);
    const manifest = await this.json<unknown>(res);
    return { kind: 'ok', manifest, etag: res.headers.get('ETag') };
  }

  async heartbeat(token: string, body: HeartbeatBody): Promise<HeartbeatResponse> {
    const res = await this.request('POST', '/api/v3/heartbeat', { token, body });
    await this.checkAuthed(res);
    if (res.status !== 200) throw new HttpError(res.status, `heartbeat ${res.status}`);
    const j = await this.json<Partial<HeartbeatResponse>>(res);
    return { ...j, commands: Array.isArray(j.commands) ? j.commands : [] };
  }

  /** Proof-of-play (max 500 per richiesta). Risolve solo su 200; ogni altro esito lancia. */
  async plays(token: string, plays: unknown[]): Promise<void> {
    const res = await this.request('POST', '/api/v3/plays', { token, body: { plays } });
    await this.checkAuthed(res);
    if (res.status !== 200) throw new HttpError(res.status, `plays ${res.status}`);
  }

  /** Cosa è in onda adesso (pagina "Live"). Timeout 5 s, nessun retry; 204 = ok. */
  async now(token: string, body: NowBody): Promise<void> {
    const res = await this.request('POST', '/api/v3/now', { token, body, timeoutMs: 5_000 });
    await this.checkAuthed(res);
    if (res.status !== 204 && res.status !== 200) throw new HttpError(res.status, `now ${res.status}`);
  }

  /** Screenshot JPEG (multipart: `image` + `command_id` facoltativo). Risposta attesa 201. */
  async screenshot(token: string, image: Blob, commandId?: number): Promise<void> {
    const form = new FormData();
    form.append('image', image, 'screenshot.jpg');
    if (commandId !== undefined) form.append('command_id', String(commandId));
    const res = await this.request('POST', '/api/v3/screenshot', { token, form });
    await this.checkAuthed(res);
    if (res.status !== 201 && res.status !== 200) throw new HttpError(res.status, `screenshot ${res.status}`);
  }

  async time(): Promise<number> {
    const res = await this.request('GET', '/api/v3/time');
    if (res.status !== 200) throw new HttpError(res.status, `time ${res.status}`);
    const j = await this.json<{ server_time_ms?: number }>(res);
    if (typeof j.server_time_ms !== 'number') throw new NetworkError('server_time_ms mancante');
    return j.server_time_ms;
  }
}
